import { theme } from '@prophecy/theme';
import { Text, RadioGroup, Stack } from '@prophecy/ui-v3';
import styled from 'styled-components';

interface OnboardingQuestionProps {
  question: string;
  answer: string;
  options: Array<string>;

  onChange: (value: string) => void;
}

/**
 * OnboardingQuestion is a component that displays a question and a radio group of options.
 * @param question - The question to display.
 * @param answer - The answer to display.
 * @param options - The options to display.
 * @param onChange - The function to call when the answer changes.
 * @returns A component that displays a question and a radio group of options.
 */

export const OnboardingQuestion = ({ question, answer, options, onChange }: OnboardingQuestionProps) => {
  return (
    <Stack direction='vertical' gap={theme.spaces.x10} width='100%' padding={`${theme.spaces.x8} 0`}>
      <StyledQuestionText level='md'>{question}</StyledQuestionText>
      <RadioGroup orientation='horizontal' onValueChange={onChange} value={answer} asChild>
        <StyledRadioGroupItemWrapper>
          {options.map((option) => (
            <StyledRadioGroupItem key={option} value={option} label={option} isCard={true}>
              {option}
            </StyledRadioGroupItem>
          ))}
        </StyledRadioGroupItemWrapper>
      </RadioGroup>
    </Stack>
  );
};

const StyledRadioGroupItemWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spaces.x24};
  width: 100%;
`;

const StyledRadioGroupItem = styled(RadioGroup.Item)`
  width: 100%;

  &&& {
    height: 75px;
  }

  &:hover {
    background: ${theme.colors.brand25};
    border-color: ${theme.colors.brand300};
  }
`;

const StyledQuestionText = styled(Text)`
  color: ${theme.colors.gray800};
  font-weight: ${theme.fontWeight.medium};
`;
